import { IProjectRole } from "@kuika/kuika-cl-model";
import { useCallback, useState } from "react";
import { getSdInstance } from "../../../../../shared/utility/designer-context";
import {
  addDesignerItemToFolder,
  addDesignerItemToSubfolder,
  removeDesignerItemFromFolder,
  removeDesignerItemFromSubfolder
} from "../../../../../shared/utility/designer-folder-actions";
import { DragPreview } from "./role-management-modal";

export const useDragAndDrop = (sd: any, roles: IProjectRole[], roleFolders: any[]) => {
  const [dragPreview, setDragPreview] = useState<DragPreview | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const onDragStart = useCallback(
    (start: any) => {
      const role = roles.find((r) => r.id === start.draggableId);
      if (!role) return;

      setDragPreview({ role, x: mousePosition.x, y: mousePosition.y });

      const handleMouseMove = (e: MouseEvent) => {
        const newPosition = { x: e.clientX, y: e.clientY };
        setMousePosition(newPosition);
        setDragPreview((prev) => (prev ? { ...prev, x: e.clientX + 10, y: e.clientY - 10 } : null));
      };

      const cleanup = () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", cleanup);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", cleanup);
    },
    [roles, mousePosition]
  );

  const onDragEnd = useCallback(
    (result: any) => {
      setDragPreview(null);
      const { destination, draggableId } = result;
      if (!destination) return;

      const dstDroppable = destination.droppableId;

      if (dstDroppable === "droppable-root") {
        const currentFolder = sd.findRoleFolderByRoleId?.(draggableId, roleFolders);
        if (currentFolder) {
          if (currentFolder.parentFolderId) {
            removeDesignerItemFromSubfolder(getSdInstance(), {
              folderType: "role",
              parentFolderId: currentFolder.parentFolderId,
              subFolderId: currentFolder.id,
              itemId: draggableId
            });
          } else {
            removeDesignerItemFromFolder(getSdInstance(), {
              folderType: "role",
              folderId: currentFolder.id,
              itemId: draggableId
            });
          }
        }
        return;
      }

      const destFolderInfo = sd.getRoleFolderWithParent?.(dstDroppable, roleFolders);

      if (!destFolderInfo) {
        return;
      }

      const { folder: destFolder, parent: destParent } = destFolderInfo;

      const currentFolder = sd.findRoleFolderByRoleId?.(draggableId, roleFolders);
      if (currentFolder) {
        if (currentFolder.parentFolderId) {
          removeDesignerItemFromSubfolder(getSdInstance(), {
            folderType: "role",
            parentFolderId: currentFolder.parentFolderId,
            subFolderId: currentFolder.id,
            itemId: draggableId
          });
        } else {
          removeDesignerItemFromFolder(getSdInstance(), {
            folderType: "role",
            folderId: currentFolder.id,
            itemId: draggableId
          });
        }
      }

      if (destParent) {
        addDesignerItemToSubfolder(getSdInstance(), {
          folderType: "role",
          parentFolderId: destParent.id,
          subFolderId: destFolder.id,
          itemId: draggableId
        });
      } else {
        addDesignerItemToFolder(getSdInstance(), {
          folderType: "role",
          folderId: destFolder.id,
          itemId: draggableId
        });
      }
    },
    [sd, roleFolders]
  );

  return { dragPreview, onDragStart, onDragEnd };
};
